/**
 * useJourney — loads a journey by token, tracks the focused entry index, and
 * exposes a token-scoped respond() that optimistically records client actions.
 */

import { useState, useEffect, useCallback } from 'react'
import { getJourney, respond as respondApi, verifyJourneyPin, setCachedPinProof, requestPinReset } from '../services/journey.js'
import { orderEntries } from './journeyOrder.js'

export function useJourney(token) {
  const [status, setStatus]   = useState('loading')  // loading | ready | notfound | error | pin_required | pin_locked
  const [pinError, setPinError] = useState(null)      // null | 'pin_invalid' shown once, next attempt clears it
  const [pinRetryAt, setPinRetryAt] = useState(null)  // set when status === 'pin_locked'
  const [pinBusy, setPinBusy] = useState(false)
  const [isStaffView, setIsStaffView] = useState(false)
  const [subscribedEmail, setSubscribedEmail] = useState(null)  // the email getting update emails, if any
  const [recipientName, setRecipientName] = useState(null)  // whose Hub this is (a preview shows it in its banner)
  const [recipientId, setRecipientId] = useState(null)  // this viewer's own journey_recipients.id, null for a legacy shared-link visit or staff preview
  const [tour, setTour] = useState(null)   // Getting started tour (docs/CLIENT_HUB_TOUR.md); null = legacy shared link
  const [project, setProject] = useState(null)
  const [entries, setEntries] = useState([])
  const [responses, setResponses] = useState([])     // array of { entry_id, kind, payload, ... }
  const [phases, setPhases]   = useState([])          // ordered project phase names (roadmap branches)
  const [index, setIndex]     = useState(0)

  const load = useCallback(() => {
    let cancelled = false
    if (!token) { setStatus('notfound'); return () => {} }
    setStatus('loading')
    getJourney(token)
      .then(data => {
        if (cancelled) return
        const ordered = orderEntries(data.entries || [], data.phases || [])
        // viewerRole: 'owners_rep' sees no money or signing and cannot answer for the owner.
        setProject(data.project ? { ...data.project, viewerRole: data.viewerRole || 'client' } : data.project)
        setEntries(ordered)
        setResponses(data.responses || [])
        setPhases(data.phases || [])
        setIsStaffView(!!data.isStaffView)
        setRecipientId(data.recipientId || null)
        setRecipientName(data.recipientName || null)
        setSubscribedEmail(data.subscribedEmail || null)
        setTour(data.tour || null)
        // Open to the newest ROADMAP entry (not an agreement). Agreements (ASRs + secondary
        // proposals) are excluded from the carousel; landing on one would show the first card.
        // Mirror the isAgreement logic from JourneyShell: ASRs are always agreements; the first
        // proposal is the roadmap opener, every subsequent proposal is an agreement.
        const firstProposalIdx = ordered.findIndex(e => e.type === 'proposal')
        const isAgr = (e, i) => e.type === 'asr_review' || (e.type === 'proposal' && i !== firstProposalIdx)
        const lastRoadmapIdx = ordered.reduce((last, e, i) => (!isAgr(e, i) ? i : last), 0)
        // Open on the newest update this person hasn't seen yet (Matt, 2026-09-23) — a living card
        // like Permitting updates in place in its roadmap spot, so without this a client opening the
        // Journey after an update could land on a later card and have to hunt for it. The roadmap
        // order itself is unchanged. No unseen updates: the last roadmap card, as before.
        let newestIdx = -1, newestAt = ''
        ordered.forEach((e, i) => {
          if (!e.isNew || isAgr(e, i)) return
          const at = String(e.published_at || '')
          if (at > newestAt) { newestAt = at; newestIdx = i }
        })
        setIndex(newestIdx >= 0 ? newestIdx : lastRoadmapIdx)
        setStatus('ready')
      })
      .catch(err => {
        if (cancelled) return
        if (err.code === 'pin_required' || err.code === 'pin_invalid') {
          setPinError(err.code === 'pin_invalid' ? 'pin_invalid' : null)
          setStatus('pin_required')
        } else if (err.code === 'locked') {
          setPinRetryAt(err.retryAt || null)
          setStatus('pin_locked')
        } else {
          setStatus(err.code === 'not_found' ? 'notfound' : 'error')
        }
      })
    return () => { cancelled = true }
  }, [token])

  useEffect(() => load(), [load])

  /** Submit a PIN attempt. Reloads the journey on success; sets pinError/pin_locked on failure. */
  const submitPin = useCallback(async (pin) => {
    setPinBusy(true)
    try {
      const proof = await verifyJourneyPin(token, pin)
      setCachedPinProof(token, proof)
      setPinError(null)
      load()
    } catch (err) {
      if (err.code === 'locked') { setPinRetryAt(err.retryAt || null); setStatus('pin_locked') }
      else setPinError('pin_invalid')
    } finally {
      setPinBusy(false)
    }
  }, [token, load])

  /** Self-service "send me a new code" — see api/journey-pin.js's requestReset action. Re-throws
   *  on failure (JourneyPinGate reads err.code) rather than swallowing it, since the caller shows
   *  its own success/error state for this specific action. */
  const requestReset = useCallback(() => requestPinReset(token), [token])

  /** Latest response for an entry, scoped to what THIS viewer has done — not whatever the
   *  latest response from ANY recipient is — so a joint proposal's second signer isn't shown
   *  the first signer's "already signed" state. Staff preview (isStaffView) is deliberately
   *  left unscoped: staff want to see the document's real aggregate state, not "have I, as no
   *  particular recipient, signed this." */
  const responseFor = useCallback(
    (entryId) => {
      // A 'homework_undo' row takes an answer back (the survey / as-built cards' "Change my answer"):
      // the latest row wins, so an undo reads as no answer at all.
      const last = rows => { const r = rows[rows.length - 1]; return r.kind === 'homework_undo' ? null : r }
      const mine = responses.filter(r => r.entry_id === entryId && (isStaffView || r.recipient_id === recipientId))
      if (mine.length) return last(mine)
      // An owner's rep sees how the owner answered (docs/CLIENT_HUB_OWNERS_REP.md); the server only
      // sends him responses on entries he may see.
      if (project && project.viewerRole === 'owners_rep') {
        const any = responses.filter(r => r.entry_id === entryId)
        return any.length ? last(any) : null
      }
      return null
    },
    [responses, recipientId, isStaffView, project]
  )

  /** Record a client action against the focused entry. Optimistic. */
  const respond = useCallback(
    async (entryId, kind, payload = {}) => {
      // optimistic local row — carries this viewer's own recipient_id so responseFor's scoping
      // matches it immediately, same as the real row the server will return.
      const optimistic = { id: 'tmp-' + Date.now(), entry_id: entryId, kind, payload, recipient_id: recipientId, _optimistic: true }
      setResponses(prev => [...prev, optimistic])
      try {
        const saved = await respondApi(token, entryId, kind, payload)
        setResponses(prev => prev.map(r => (r === optimistic ? { recipient_id: recipientId, ...(saved.response || saved) } : r)))
      } catch {
        // roll back on failure
        setResponses(prev => prev.filter(r => r !== optimistic))
      }
    },
    [token, recipientId]
  )

  const total = entries.length
  const goNext = useCallback(() => setIndex(i => Math.min(i + 1, total - 1)), [total])
  const goPrev = useCallback(() => setIndex(i => Math.max(i - 1, 0)), [])
  const goTo   = useCallback((i) => setIndex(() => Math.max(0, Math.min(i, total - 1))), [total])

  return {
    status, project, entries, phases, index, total, responseFor, respond, goNext, goPrev, goTo,
    isStaffView, recipientName, recipientId, subscribedEmail, tour, setTour, submitPin, pinBusy, pinError, pinRetryAt, requestReset,
  }
}
